import mime from "mime-types";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useState,
} from "react";
import { EUFinanceFileStoreGetFileMetadata } from "../../models/metadata/EUFinanceFilestoreGetFileMetadata";
import { EUFinanceCaseAttachment } from "../../models/sobjects/EUFinanceCaseAttachment";
import { useEUFinanceFileStoreService } from "../../services/EUFinanceFileStoreService";
import { OpenFileHandler } from "../../utils/FileUtil";

export type UseDownloadCaseAttachmentOptions = {
  /**
   * @deprecated legacy prop
   */
  setIsDownloadingFileByFileIdMap?: Dispatch<
    SetStateAction<Record<string, boolean>>
  >;
  openFile?: OpenFileHandler;
  onError?: (message: ReactNode) => void;
  onDownloadStart?: (message: ReactNode) => void;
  onDownloadComplete?: (message: ReactNode) => void;
};

export type UseDownloadCaseAttachmentResult = {
  isLoading: boolean;
  downloadFile: (file: EUFinanceCaseAttachment) => Promise<void>;
  blob: Blob | undefined;
  fileExtension: string | undefined;
  fileName: string | undefined;
  mimeType: string | undefined;
  errorMessage: ReactNode;
};

const useDownloadCaseAttachment = ({
  setIsDownloadingFileByFileIdMap,
  openFile,
  onError,
  onDownloadStart,
  onDownloadComplete,
}: UseDownloadCaseAttachmentOptions): UseDownloadCaseAttachmentResult => {
  const fileStoreService = useEUFinanceFileStoreService();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [blob, setBlob] = useState<Blob>();
  const [fileExtension, setFileExtension] = useState<string>();
  const [fileName, setFileName] = useState<string>();
  const [mimeType, setMimeType] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<ReactNode>();

  const handleError = useCallback(
    (message: ReactNode) => {
      setErrorMessage(message);
      onError?.(message);
    },
    [onError]
  );

  const handleDownloadStart = useCallback(
    (message: ReactNode) => {
      onDownloadStart?.(message);
    },
    [onDownloadStart]
  );

  const handleDownloadComplete = useCallback(
    (message: ReactNode) => {
      onDownloadComplete?.(message);
    },
    [onDownloadComplete]
  );

  const downloadFile = useCallback(
    async (file: EUFinanceCaseAttachment) => {
      if (!file.fileName || !file.filePath) {
        handleError("File information is missing");
        return;
      }
      setIsDownloadingFileByFileIdMap?.(
        (previousisDownloadingFileByFileIdMap) => ({
          ...previousisDownloadingFileByFileIdMap,
          [file.id as string]: true,
        })
      );
      setIsLoading(true);
      try {
        const newFileExtension = file.fileName.split(".").reverse()[0];
        const newMimeType = (mime.lookup(file.fileName) ??
          "application/octet-stream") as string;
        if (!file.id) {
          handleError(`Failed to load file: ${file.fileName}`);
        } else {
          handleDownloadStart(`Start loading file: ${file.fileName}`);
          const getFileResponse =
            await fileStoreService.getFileByKeyAndMetadata(file.id, {
              caseId: file.caseId,
              fileName: file.fileName,
              filePath: file.filePath,
            } as EUFinanceFileStoreGetFileMetadata);
          if (getFileResponse?.content) {
            const newBlob = getFileResponse.content;
            const newFileName = file.fileName;
            setBlob(newBlob);
            setFileExtension(newFileExtension);
            setFileName(file.fileName);
            setMimeType(newMimeType);
            openFile?.(newBlob, newFileExtension, newFileName, newMimeType);
            handleDownloadComplete(`Finished loading file: ${file.fileName}`);
          } else {
            handleError(`Failed to load file: ${file.fileName}`);
          }
        }
      } catch (error) {
        handleError(`An error occurred while loading file: ${file.fileName}`);
      } finally {
        setIsDownloadingFileByFileIdMap?.(
          (previousisDownloadingFileByFileIdMap) => ({
            ...previousisDownloadingFileByFileIdMap,
            [file.id as string]: false,
          })
        );
        setIsLoading(false);
      }
    },
    [
      fileStoreService,
      handleDownloadComplete,
      handleDownloadStart,
      handleError,
      openFile,
      setIsDownloadingFileByFileIdMap,
    ]
  );

  return {
    isLoading,
    downloadFile,
    blob,
    fileExtension,
    fileName,
    mimeType,
    errorMessage,
  };
};

export default useDownloadCaseAttachment;
